import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { withRouter } from 'react-router-dom';
import StringField from 'Common/Components/StringField';
import {
  setField, addPolicy, removePolicy, addReference,
  removeReference, addCVE, removeCVE
} from 'Screens/Contextualization/KnowledgeBaseEditCreate/actions/Actions';
import {
  selectTemplateEditCreate,
  selectReadonly
} from 'store/KnowledgeBase/selectors';
import SeverityDropdown from 'Common/Components/SeverityDropdown';
import EORDropdown from 'Common/Components/EORDropdown';
import CustomList from 'Common/Components/CustomList';
import Impacts from 'Common/Components/Impacts';
import {
  Wrapper, DropdownsContainer, RedDot, Black, Row, LeftColumn, RightColumn, Box
} from './styled';
import { selectAllowedByRole } from 'Hooks/usePermission/selector';
import { FEATURE_VULN_TEMPLATE, PERMISSION_TYPES } from 'Hooks/usePermission/permissions';
import { selectRiskScoreFeatureFlag } from 'store/Faraday/selectors';
import MarkdownInput from 'Screens/Contextualization/ManageEditCreate/components/ManualCreationModal/components/MarkdownInput';
import CVE from './InputCVE';
import CVSS from './components/CVSS';
import { eorDropdownStyleKB, severityDropdownStyleKB } from 'Common/styles/style';
import { useAutoScrollOnHeightIncrease } from 'Hooks/useAutoScrollOnHeightIncrease';
import { SectionTitle } from 'Common/Components/Impacts/styled';

const General = ({ setFieldError }) => {
  const dispatch = useDispatch();
  const wrapperRef = useRef(null);
  useAutoScrollOnHeightIncrease(wrapperRef);

  const props = useSelector((state) => ({ ...selectReadonly(state), ...selectTemplateEditCreate(state) }));

  const saveData = (field, value) => {
    dispatch(setField(field, value));
  };

  const setCVE = (val) => {
    dispatch(addCVE(val));
  };

  const removeCVEFromKB = (val) => {
    dispatch(removeCVE(val));
  };

  const impacts = {
    accountability: props.accountability,
    availability: props.availability,
    confidentiality: props.confidentiality,
    integrity: props.integrity
  };

  const canUpdate = useSelector((state) => selectAllowedByRole(state, FEATURE_VULN_TEMPLATE, PERMISSION_TYPES.CREATE));
  const isAllowedByLicense = useSelector(selectRiskScoreFeatureFlag);

  return (
    <Wrapper ref={ wrapperRef }>
      <Row>
        <LeftColumn>
          <StringField
            width="100%"
            height="38px"
            placeholder="Vulnerability template name"
            defaultValue={ props.name }
            onChange={ (value) => saveData('name', value) }
            title={ (
              <RedDot>
                <Black>Name</Black>
                {' '}
                *
              </RedDot>
            ) }
          />
        </LeftColumn>
        <RightColumn>
          <DropdownsContainer>
            <SeverityDropdown
              updateValue={ (field, value) => saveData('exploitation', value) }
              defaultValue={ props.exploitation }
              customStyle={ severityDropdownStyleKB }
              context
            />
            <EORDropdown
              updateValue={ (field, value) => saveData('easeofresolution', value) }
              defaultValue={ props.easeofresolution }
              customStyle={ eorDropdownStyleKB }
            />
          </DropdownsContainer>
        </RightColumn>
      </Row>

      <Row className="ml-0 mr-0 margin-top-26">
        <LeftColumn>
          <Box>
            <MarkdownInput
              title="Description"
              value={ props.description }
              onBlur={ (value) => saveData('description', value) }
              width='100%'
            />
          </Box>
        </LeftColumn>
        <RightColumn>
          <Box>
            <MarkdownInput
              title="Resolution"
              value={ props.resolution }
              onBlur={ (value) => saveData('resolution', value) }
            />
          </Box>
        </RightColumn>
      </Row>

      <Row className="ml-0 mr-0 margin-top-26">
        <LeftColumn>
          <StringField
            width="100%"
            height="38px"
            placeholder="125798926"
            title="External ID"
            value={ props.external_id }
            onChange={ (value) => dispatch(setField('external_id', value)) }
            defaultValue={ props.external_id }
          />
        </LeftColumn>
        <RightColumn>
          <Impacts
            selectCheckbox={ (field) => saveData(field, !props[field]) }
            impacts={ impacts }
            margin={ false }
            width='100%'
          />
        </RightColumn>
      </Row>

      <Row className="ml-0 mr-0 margin-top-26">
        <LeftColumn>
          <div>
            <SectionTitle>Policy Violations</SectionTitle>
            <CustomList
              key="CL_POLICY_VIOLATIONS"
              placeholder="Add a Policy Violation"
              addItem={ () => dispatch(addPolicy()) }
              removeItem={ (index) => dispatch(removePolicy(index)) }
              setItem={ (value) => saveData('policy', value) }
              listData={ props.policyviolations }
              canUpdate={ canUpdate }
              height={ '36px' }
              spanStyle='icon2-container'
              responsive
              testIdPrefix="kb-policy-violation"
            />
          </div>
        </LeftColumn>
        <RightColumn>
          <div>
            <SectionTitle>References</SectionTitle>
            <CustomList
              key="CL_REFERENCES"
              placeholder="Add a Reference"
              addItem={ () => dispatch(addReference()) }
              removeItem={ (index) => dispatch(removeReference(index)) }
              setItem={ (value) => dispatch(setField('reference', value)) }
              listData={ props.references }
              canUpdate={ canUpdate }
              height={ '36px' }
              spanStyle='icon2-container'
              responsive
              testIdPrefix="kb-reference"
            />
          </div>
        </RightColumn>
      </Row>

      <Row className="ml-0 mr-0 margin-top-26">
        <LeftColumn>
          <CVE
            width='100%'
            listData={ props.cve }
            onAdd= { setCVE }
            onRemove={ removeCVEFromKB }
            onValidationChange={ (hasError) => setFieldError('cve', hasError) }
          />
        </LeftColumn>
        <RightColumn>
          { isAllowedByLicense && <CVSS data={ props } onValidationChange={ (hasError) => setFieldError('cvss', hasError) } /> }
        </RightColumn>
      </Row>
    </Wrapper>
  );
};

General.propTypes = {
  setFieldError: PropTypes.func
};

General.defaultProps = {
  setFieldError: () => {}
};

export default withRouter(General);
